import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useUserChannels } from "@/hooks/useUserChannels";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Hash, Users, Megaphone, Handshake, Search, Mail, Users as UsersIcon } from "lucide-react";

interface InviteColleagueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultChannelId?: string;
  defaultChannelName?: string;
}

export const InviteColleagueDialog = ({
  open,
  onOpenChange,
  defaultChannelId,
  defaultChannelName,
}: InviteColleagueDialogProps) => {
  // Tab state
  const [activeTab, setActiveTab] = useState<"platform" | "email">("platform");
  
  // Platform user selection state
  const [selectedPlatformUserId, setSelectedPlatformUserId] = useState<string | null>(null);
  const [selectedPlatformUser, setSelectedPlatformUser] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Email invitation state (existing)
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [consentChecked, setConsentChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [remainingInvites, setRemainingInvites] = useState<number | null>(null);
  
  // Invitation type state
  const [inviteToChannel, setInviteToChannel] = useState(!!defaultChannelId);
  const [selectedChannelId, setSelectedChannelId] = useState(defaultChannelId || "");
  const [selectedChannelName, setSelectedChannelName] = useState(defaultChannelName || "");
  const [inviteToNetwork, setInviteToNetwork] = useState(false);
  const [customMessage, setCustomMessage] = useState("");
  
  // Call and Sprint invitation state
  const [inviteToCall, setInviteToCall] = useState(false);
  const [selectedCallId, setSelectedCallId] = useState("");
  const [selectedCallTitle, setSelectedCallTitle] = useState("");
  const [inviteToSprint, setInviteToSprint] = useState(false);
  const [selectedSprintId, setSelectedSprintId] = useState("");
  const [selectedSprintObjective, setSelectedSprintObjective] = useState("");
  
  const { toast } = useToast();
  const { user } = useAuth();
  const { data: userChannels } = useUserChannels(user?.id);

  // Fetch all platform users (excluding self)
  const { data: platformUsers = [], isLoading: loadingUsers } = useQuery({
    queryKey: ['all-profiles-invite', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data } = await supabase
        .from('profiles')
        .select('id, display_name, username, avatar_url, title, organization, email')
        .neq('id', user.id)
        .order('display_name');
      return data || [];
    },
    enabled: !!user?.id && open,
  });

  // Fetch calls where user is creator (owner)
  const { data: userCalls } = useQuery({
    queryKey: ['user-owned-calls', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data } = await supabase
        .from('calls')
        .select('id, title, status')
        .eq('creator_id', user.id)
        .in('status', ['open', 'in_progress'])
        .order('created_at', { ascending: false });
      return data || [];
    },
    enabled: !!user?.id && open,
  });

  // Fetch sprints where user is creator
  const { data: userSprints } = useQuery({
    queryKey: ['user-owned-sprints', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data } = await supabase
        .from('prompt_architecture_sprints')
        .select('id, objective, status')
        .eq('creator_id', user.id)
        .in('status', ['active', 'in_progress'])
        .order('created_at', { ascending: false });
      return data || [];
    },
    enabled: !!user?.id && open,
  });

  // Filter platform users based on search term
  const filteredUsers = platformUsers.filter(user =>
    user.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.organization?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Reset form when dialog opens/closes or defaults change
  useEffect(() => {
    if (open) {
      setActiveTab("platform");
      setSelectedPlatformUserId(null);
      setSelectedPlatformUser(null);
      setSearchTerm("");
      setInviteToChannel(!!defaultChannelId);
      setSelectedChannelId(defaultChannelId || "");
      setSelectedChannelName(defaultChannelName || "");
      setInviteToCall(false);
      setSelectedCallId("");
      setSelectedCallTitle("");
      setInviteToSprint(false);
      setSelectedSprintId("");
      setSelectedSprintObjective("");
    }
  }, [open, defaultChannelId, defaultChannelName]);

  const handleChannelChange = (channelId: string) => {
    setSelectedChannelId(channelId);
    const channel = userChannels?.find(c => c.channel_id === channelId);
    setSelectedChannelName(channel?.channels?.name || "");
  };

  const handleCallChange = (callId: string) => {
    setSelectedCallId(callId);
    const call = userCalls?.find(c => c.id === callId);
    setSelectedCallTitle(call?.title || "");
  };

  const handleSprintChange = (sprintId: string) => {
    setSelectedSprintId(sprintId);
    const sprint = userSprints?.find(s => s.id === sprintId);
    setSelectedSprintObjective(sprint?.objective || "");
  };

  const handleSelectPlatformUser = (platformUser: any) => {
    if (selectedPlatformUserId === platformUser.id) {
      setSelectedPlatformUserId(null);
      setSelectedPlatformUser(null);
    } else {
      setSelectedPlatformUserId(platformUser.id);
      setSelectedPlatformUser(platformUser);
    }
  };

  const handlePlatformUserInvitation = async () => {
    if (!selectedPlatformUserId || !user?.id) return;

    setIsLoading(true);
    try {
      // Get inviter profile for notification messages
      const { data: inviterProfile } = await supabase
        .from('profiles')
        .select('display_name')
        .eq('id', user.id)
        .single();
      
      const inviterName = inviterProfile?.display_name || 'A colleague';

      // Handle network invite - direct add (bidirectional)
      if (inviteToNetwork) {
        // Add to inviter's network
        await supabase.from('user_networks').upsert({
          user_id: user.id,
          network_member_id: selectedPlatformUserId,
          added_via: 'invitation'
        }, { onConflict: 'user_id,network_member_id' });
        
        // Add reverse connection
        await supabase.from('user_networks').upsert({
          user_id: selectedPlatformUserId,
          network_member_id: user.id,
          added_via: 'invitation'
        }, { onConflict: 'user_id,network_member_id' });

        // Create notification for the invitee
        await supabase.from('notifications').insert({
          user_id: selectedPlatformUserId,
          type: 'network_invitation',
          title: 'Added to Knowledge Network',
          message: `${inviterName} added you to their Knowledge Network`,
          metadata: {
            inviter_id: user.id,
            inviter_name: inviterName,
            custom_message: customMessage || null
          }
        });
      }

      // Handle channel invite - create notification
      if (inviteToChannel && selectedChannelId) {
        await supabase.from('notifications').insert({
          user_id: selectedPlatformUserId,
          type: 'channel_invitation',
          title: 'Channel Invitation',
          message: `${inviterName} invited you to join ${selectedChannelName}`,
          action_url: `/accept-channel?channel_id=${selectedChannelId}`,
          metadata: {
            channel_id: selectedChannelId,
            channel_name: selectedChannelName,
            inviter_id: user.id,
            inviter_name: inviterName,
            custom_message: customMessage || null
          }
        });
      }

      // Handle call invite - create notification
      if (inviteToCall && selectedCallId) {
        await supabase.from('notifications').insert({
          user_id: selectedPlatformUserId,
          type: 'call_invitation',
          title: 'Call Invitation',
          message: `${inviterName} invited you to participate in "${selectedCallTitle}"`,
          action_url: `/calls/${selectedCallId}`,
          metadata: {
            call_id: selectedCallId,
            call_title: selectedCallTitle,
            inviter_id: user.id,
            inviter_name: inviterName,
            custom_message: customMessage || null
          }
        });
      }

      // Handle sprint invite - add to sprint_invitations and create notification
      if (inviteToSprint && selectedSprintId) {
        // Create sprint invitation record
        await supabase.from('sprint_invitations').insert({
          sprint_id: selectedSprintId,
          inviter_id: user.id,
          invitee_email: selectedPlatformUser.email,
          invitee_user_id: selectedPlatformUserId,
          invitee_name: selectedPlatformUser.display_name,
          invitation_message: customMessage || null,
          status: 'pending'
        });

        // Create notification
        await supabase.from('notifications').insert({
          user_id: selectedPlatformUserId,
          type: 'sprint_invitation',
          title: 'Sprint Invitation',
          message: `${inviterName} invited you to join a sprint: "${selectedSprintObjective.substring(0, 50)}${selectedSprintObjective.length > 50 ? '...' : ''}"`,
          action_url: `/sprints/${selectedSprintId}`,
          metadata: {
            sprint_id: selectedSprintId,
            sprint_objective: selectedSprintObjective,
            inviter_id: user.id,
            inviter_name: inviterName,
            custom_message: customMessage || null
          }
        });
      }

      // Build success message
      const inviteTypes: string[] = [];
      if (inviteToChannel) inviteTypes.push(`${selectedChannelName} channel`);
      if (inviteToNetwork) inviteTypes.push("Knowledge Network");
      if (inviteToCall) inviteTypes.push(`"${selectedCallTitle}" call`);
      if (inviteToSprint) inviteTypes.push(`"${selectedSprintObjective.substring(0, 30)}${selectedSprintObjective.length > 30 ? '...' : ''}" sprint`);

      toast({
        title: "Invitation sent!",
        description: `Successfully invited ${selectedPlatformUser.display_name} to: ${inviteTypes.join(", ")}.`,
      });

      // Reset and close
      resetForm();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Platform invitation error:', error);
      toast({
        title: "Failed to send invitation",
        description: error.message || "An error occurred while sending the invitation",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailInvitation = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !consentChecked) {
      toast({
        title: "Missing information",
        description: "Please fill in the email and accept the privacy notice",
        variant: "destructive",
      });
      return;
    }

    // Validate at least one invitation type
    if (!inviteToChannel && !inviteToNetwork && !inviteToCall && !inviteToSprint) {
      toast({
        title: "Select invitation type",
        description: "Please select at least one invitation type",
        variant: "destructive",
      });
      return;
    }

    // Validate selections for each type
    if (inviteToChannel && !selectedChannelId) {
      toast({
        title: "Select a channel",
        description: "Please select a channel to invite the colleague to",
        variant: "destructive",
      });
      return;
    }

    if (inviteToCall && !selectedCallId) {
      toast({
        title: "Select a call",
        description: "Please select a call to invite the colleague to",
        variant: "destructive",
      });
      return;
    }

    if (inviteToSprint && !selectedSprintId) {
      toast({
        title: "Select a sprint",
        description: "Please select a sprint to invite the colleague to",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Handle sprint invitation separately using send-sprint-invitation
      if (inviteToSprint && selectedSprintId) {
        const { data: sprintData, error: sprintError } = await supabase.functions.invoke("send-sprint-invitation", {
          body: {
            sprint_id: selectedSprintId,
            invitee_emails: [email],
            custom_message: customMessage || undefined,
          },
        });

        if (sprintError) {
          console.error('Sprint invitation error:', sprintError);
          throw new Error(sprintError.message || 'Failed to send sprint invitation');
        }

        if (sprintData?.error) {
          throw new Error(sprintData.error);
        }
      }

      // Handle channel/network/call invitation
      if (inviteToChannel || inviteToNetwork || inviteToCall) {
        const { data, error } = await supabase.functions.invoke("send-invitation", {
          body: {
            invitee_email: email,
            invitee_name: name || undefined,
            channel_id: inviteToChannel ? selectedChannelId : undefined,
            channel_name: inviteToChannel ? selectedChannelName : undefined,
            include_network_invite: inviteToNetwork,
            call_id: inviteToCall ? selectedCallId : undefined,
            call_title: inviteToCall ? selectedCallTitle : undefined,
            custom_message: customMessage || undefined,
          },
        });

        if (error) {
          console.error('Edge function error:', error);
          throw new Error(error.message || 'Failed to send invitation');
        }

        if (data?.error) {
          throw new Error(data.error);
        }

        if (!data?.success) {
          throw new Error('Unexpected response from invitation service');
        }

        setRemainingInvites(data.remaining_invites);
      }

      // Build success message
      const inviteTypes: string[] = [];
      if (inviteToChannel) inviteTypes.push(`${selectedChannelName} channel`);
      if (inviteToNetwork) inviteTypes.push("Knowledge Network");
      if (inviteToCall) inviteTypes.push(`"${selectedCallTitle}" call`);
      if (inviteToSprint) inviteTypes.push(`"${selectedSprintObjective.substring(0, 30)}${selectedSprintObjective.length > 30 ? '...' : ''}" sprint`);

      toast({
        title: "Invitation sent!",
        description: `Successfully sent invitation to ${email} for: ${inviteTypes.join(", ")}.`,
      });

      // Reset and close
      resetForm();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: "Failed to send invitation",
        description: error.message || "An error occurred while sending the invitation",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setEmail("");
    setName("");
    setConsentChecked(false);
    setCustomMessage("");
    setInviteToNetwork(false);
    setInviteToCall(false);
    setSelectedCallId("");
    setSelectedCallTitle("");
    setInviteToSprint(false);
    setSelectedSprintId("");
    setSelectedSprintObjective("");
    setSelectedPlatformUserId(null);
    setSelectedPlatformUser(null);
    setSearchTerm("");
    if (!defaultChannelId) {
      setInviteToChannel(false);
      setSelectedChannelId("");
      setSelectedChannelName("");
    }
  };

  const hasAtLeastOneType = inviteToChannel || inviteToNetwork || inviteToCall || inviteToSprint;
  const channelValid = !inviteToChannel || selectedChannelId;
  const callValid = !inviteToCall || selectedCallId;
  const sprintValid = !inviteToSprint || selectedSprintId;
  
  // Platform tab validation
  const canSubmitPlatform = selectedPlatformUserId && hasAtLeastOneType && channelValid && callValid && sprintValid;
  
  // Email tab validation
  const canSubmitEmail = email && consentChecked && hasAtLeastOneType && channelValid && callValid && sprintValid;

  const InvitationTypeSection = () => (
    <div className="space-y-2 p-3 bg-muted/30 rounded-lg border overflow-hidden">
      <Label className="text-xs font-medium">Invitation Type</Label>
      
      {/* Channel Invitation */}
      <div className="space-y-1.5">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="inviteToChannel"
            checked={inviteToChannel}
            onCheckedChange={(checked) => setInviteToChannel(checked as boolean)}
            className="h-3.5 w-3.5"
          />
          <label
            htmlFor="inviteToChannel"
            className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-1.5"
          >
            <span className="inline-flex items-center justify-center h-4 w-4 rounded bg-green-700">
              <Hash className="h-3 w-3 text-white" />
            </span>
            Invite to Channel
          </label>
        </div>
        
        {inviteToChannel && (
          <div className="ml-5">
            <Select
              value={selectedChannelId}
              onValueChange={handleChannelChange}
            >
              <SelectTrigger className="h-8 text-sm w-full">
                <SelectValue placeholder="Select a channel" />
              </SelectTrigger>
              <SelectContent>
                {userChannels?.map((uc) => (
                  <SelectItem key={uc.channel_id} value={uc.channel_id}>
                    {uc.channels?.name || uc.organization_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Network Invitation */}
      <div className="flex items-center space-x-2">
        <Checkbox
          id="inviteToNetwork"
          checked={inviteToNetwork}
          onCheckedChange={(checked) => setInviteToNetwork(checked as boolean)}
          className="h-3.5 w-3.5"
        />
        <label
          htmlFor="inviteToNetwork"
          className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-1.5"
        >
          <span className="inline-flex items-center justify-center h-4 w-4 rounded bg-green-700">
            <Users className="h-3 w-3 text-white" />
          </span>
          Add to my Knowledge Network
        </label>
      </div>

      {/* Call Invitation */}
      <div className="space-y-1.5">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="inviteToCall"
            checked={inviteToCall}
            onCheckedChange={(checked) => setInviteToCall(checked as boolean)}
            disabled={!userCalls?.length}
            className="h-3.5 w-3.5"
          />
          <label
            htmlFor="inviteToCall"
            className={`text-xs font-medium leading-none flex items-center gap-1.5 ${!userCalls?.length ? 'opacity-50' : ''}`}
          >
            <span className="inline-flex items-center justify-center h-4 w-4 rounded bg-green-700">
              <Megaphone className="h-3 w-3 text-white" />
            </span>
            Invite to Call
            {!userCalls?.length && <span className="text-muted-foreground">(no calls)</span>}
          </label>
        </div>
        
        {inviteToCall && userCalls && userCalls.length > 0 && (
          <div className="ml-5">
            <Select
              value={selectedCallId}
              onValueChange={handleCallChange}
            >
              <SelectTrigger className="h-8 text-sm w-full">
                <SelectValue placeholder="Select a call" />
              </SelectTrigger>
              <SelectContent>
                {userCalls.map((call) => (
                  <SelectItem key={call.id} value={call.id}>
                    {call.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Sprint Invitation */}
      <div className="space-y-1.5">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="inviteToSprint"
            checked={inviteToSprint}
            onCheckedChange={(checked) => setInviteToSprint(checked as boolean)}
            disabled={!userSprints?.length}
            className="h-3.5 w-3.5"
          />
          <label
            htmlFor="inviteToSprint"
            className={`text-xs font-medium leading-none flex items-center gap-1.5 ${!userSprints?.length ? 'opacity-50' : ''}`}
          >
            <span className="inline-flex items-center justify-center h-4 w-4 rounded bg-green-700">
              <Handshake className="h-3 w-3 text-white" />
            </span>
            Invite to Sprint
            {!userSprints?.length && <span className="text-muted-foreground">(no sprints)</span>}
          </label>
        </div>
        
        {inviteToSprint && userSprints && userSprints.length > 0 && (
          <div className="ml-5">
            <Select
              value={selectedSprintId}
              onValueChange={handleSprintChange}
            >
              <SelectTrigger className="h-8 text-sm w-full">
                <SelectValue placeholder="Select a sprint" />
              </SelectTrigger>
              <SelectContent>
                {userSprints.map((sprint) => (
                  <SelectItem key={sprint.id} value={sprint.id}>
                    {sprint.objective.substring(0, 50)}{sprint.objective.length > 50 ? '...' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      
      {!hasAtLeastOneType && (
        <p className="text-[10px] text-muted-foreground ml-5">
          Select at least one option
        </p>
      )}
    </div>
  );

  const CustomMessageSection = () => (
    <div className="space-y-1.5">
      <Label htmlFor="customMessage" className="text-xs">Personal Message (optional)</Label>
      <Textarea
        id="customMessage"
        placeholder="Add a personal note to your invitation..."
        value={customMessage}
        onChange={(e) => setCustomMessage(e.target.value)}
        rows={2}
        className="resize-none text-sm"
      />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px] max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-sm">Invite a Colleague</DialogTitle>
          <DialogDescription className="text-xs">
            Send an invitation to join Giblet or add existing members
            {remainingInvites !== null && (
              <span className="block mt-1.5 font-medium text-primary">
                {remainingInvites >= 999 
                  ? "You have unlimited invitations as a Giblet team member"
                  : `You have ${remainingInvites} invitation${remainingInvites === 1 ? '' : 's'} remaining in 2025`
                }
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "platform" | "email")}>
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="platform" className="text-xs">
              <UsersIcon className="h-3 w-3 mr-1.5" /> Platform Members
            </TabsTrigger>
            <TabsTrigger value="email" className="text-xs">
              <Mail className="h-3 w-3 mr-1.5" /> Invite by Email
            </TabsTrigger>
          </TabsList>

          {/* Platform Members Tab */}
          <TabsContent value="platform" className="space-y-3 overflow-y-auto max-h-[60vh] pr-1">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search members..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 h-9 text-sm"
              />
            </div>

            {/* User List */}
            <ScrollArea className="h-[180px] border rounded-md">
              {loadingUsers ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="flex items-center justify-center h-full text-xs text-muted-foreground">
                  {searchTerm ? "No members found" : "No platform members available"}
                </div>
              ) : (
                <div className="p-2 space-y-1">
                  {filteredUsers.map((platformUser) => (
                    <div
                      key={platformUser.id}
                      onClick={() => handleSelectPlatformUser(platformUser)}
                      className={`flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors ${
                        selectedPlatformUserId === platformUser.id 
                          ? 'bg-primary/10 border border-primary/30' 
                          : 'hover:bg-muted/50'
                      }`}
                    >
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={platformUser.avatar_url} />
                        <AvatarFallback className="text-xs">
                          {platformUser.display_name?.substring(0, 2).toUpperCase() || '??'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate">{platformUser.display_name}</p>
                        <p className="text-[10px] text-muted-foreground truncate">
                          {platformUser.title && platformUser.organization 
                            ? `${platformUser.title} @ ${platformUser.organization}`
                            : platformUser.title || platformUser.organization || platformUser.username || 'Member'
                          }
                        </p>
                      </div>
                      {selectedPlatformUserId === platformUser.id && (
                        <div className="h-2 w-2 rounded-full bg-primary" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>

            {selectedPlatformUser && (
              <p className="text-xs text-muted-foreground">
                Selected: <span className="font-medium text-foreground">{selectedPlatformUser.display_name}</span>
              </p>
            )}

            <InvitationTypeSection />
            <CustomMessageSection />

            <div className="flex gap-2 justify-end pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
                size="sm"
              >
                Cancel
              </Button>
              <Button 
                onClick={handlePlatformUserInvitation}
                disabled={isLoading || !canSubmitPlatform}
                size="sm"
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Send Invitation
              </Button>
            </div>
          </TabsContent>

          {/* Email Tab (existing flow) */}
          <TabsContent value="email" className="overflow-y-auto max-h-[60vh] pr-1">
            <form onSubmit={handleEmailInvitation} className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="colleague@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-8 text-sm"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-xs">Name (optional)</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>

              <InvitationTypeSection />
              <CustomMessageSection />

              <p className="text-[10px] text-muted-foreground">
                Your message will be included in the invitation email
              </p>

              <div className="p-3 bg-muted/50 rounded-lg space-y-1.5">
                <p className="text-xs font-medium">Privacy Notice</p>
                <p className="text-[10px] text-muted-foreground">
                  During our MVP phase, Giblet tracks user engagement metrics to improve UX. 
                  Please advise invitees to avoid sharing private IP or confidential content.
                </p>
              </div>

              <div className="flex items-start space-x-2">
                <Checkbox
                  id="consent"
                  checked={consentChecked}
                  onCheckedChange={(checked) => setConsentChecked(checked as boolean)}
                  className="h-3.5 w-3.5 mt-0.5"
                />
                <label
                  htmlFor="consent"
                  className="text-[10px] leading-tight peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  I acknowledge the privacy notice and will inform the invitee about data collection practices
                </label>
              </div>

              <div className="flex gap-2 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={isLoading}
                  size="sm"
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading || !canSubmitEmail} size="sm">
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Send Invitation
                </Button>
              </div>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
